/**
 * W1-11b (app-shell-ui): the hint bar (bottom row; DESIGN.md §4.4, §5.14, §8.4, U§5 A7 "context hint bar").
 *
 * Left: what the keys do now. Gesture sub-actions set by a running gesture come first (`ui.setGestureHints`, e.g.
 * "R end hold · Esc cancel · Alt free X"); then up to MAX_HINTS bindings that would fire now (the keymap resolves
 * the chord to them in the current context and their command is enabled), ranked by `CommandDef.hint`; then the
 * focused motion column K / G / Ctrl+A act on ("Keyframes go to rotation of Main"). Settings > Hint bar:
 *  - 'sentences' (Guided default): where you are and the current tool lead the row ("Note lane, Tap tool:"), and
 *    each hint reads as a sentence when its feature wrote one: a message with the command title's key plus `Hint`
 *    (title 'notes.placeTap' -> 'notes.placeTapHint': '{keys} places a Tap at the playhead'), where `{keys}` marks
 *    the shortcut; without one the hint is the shortcut and the full title;
 *  - 'keys' (compact chips with short titles) or 'off'.
 * The visible row changes with every focus or pointer move, so it is not a live region; screen readers hear only
 * the gesture sub-actions when a gesture starts or changes (a visually hidden polite region).
 * Right: the 'statusbar' panels and status items of other modules, the Laptop layout's mini preview (F8) and drawer
 * (F9) buttons, and the shortcut sheet button.
 * The bar follows focus, hover regions, selection, tools, gestures, playback, settings and the keymap.
 *
 * W3-01 (UX-007): hints are ranked for where the user is: a placement tool's own commands and the commands of the
 * focused or hovered region come first (motion keys drop back while a note tool is active), the Chart's editing
 * commands (notes, motion, lines, tools, selection) are left out in the other workspaces, and nothing but gesture
 * sub-actions is listed while a dialog or the palette is open (its own buttons say what to do). A placement tool
 * leads with how to use it ("Click in the note lane to place a Tap"). Motion-column advice (keyframes, segments,
 * Auto-key) is listed only while a motion column or the curve editor has focus, and the Select tool leads with the
 * selection and clipboard commands. Items that do not fit are dropped whole
 * (measured after layout), never cut inside a key cap, and the focused motion column's "Keyframes go to..." note has
 * its own place after the lead and shows for a few seconds after the column changes.
 */

import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { MessageRef } from '../../core/util/types';
import { Icon } from '../../ui/Icon';
import { IconButton } from '../../ui/IconButton';
import { Kbd } from '../../ui/Kbd';
import type { AppServices, CommandDef, WhenContext } from '../registry/types';
import { DATA_REGIONS } from '../registry/when';
import type { GestureHint, ShellUiService, UiSnapshot } from '../uiService';
import { argsKey, commandState } from './commandState';
import { ErrorBoundary } from './ErrorBoundary';
import { computeChartLayout } from './LayoutPresets';
import { InlineSlot } from './SlotHost';
import {
  dependsOn,
  useEditorState,
  useKeymapVersion,
  useLayoutViewport,
  useServicesChange,
  useSettingsValue,
  useShell,
  useText,
  useTransportState,
  useUi,
  type ShellText,
} from './ShellContext';
import { m } from './shell.messages';
import s from './Shell.module.css';

export const MAX_HINTS = 8;

export interface HintItem {
  readonly key: string;
  readonly chord: string;
  readonly command: string;
  readonly title: MessageRef;
  readonly rank: number;
  /** The command's own hint sentence (CommandDef.hintSentence), if it has one. */
  readonly sentence?: string;
}

/** Command categories that only act in the Chart workspace (left out of the hints elsewhere). */
const CHART_CATEGORIES: ReadonlySet<string> = new Set(['Notes', 'Motion', 'Lines', 'Tools', 'Select']);
/** Note placement tools (their commands come first; motion keys drop back). */
const NOTE_TOOLS: ReadonlySet<string> = new Set(['tap', 'drag', 'flick', 'hold', 'eraser']);
/** Regions where the motion columns are in play (a focused column or the curve editor of one). */
const MOTION_REGIONS: ReadonlySet<string> = new Set(['motion', 'curve']);
/**
 * Motion-column advice (keyframes, segments, Auto-key): only with a motion column in play (UX-007). A Select
 * tool in the note lane otherwise reads "K adds a keyframe on the focused column" with no column focused.
 */
const MOTION_HINT_IDS: ReadonlySet<string> = new Set(['preview.toggleAutoKey']);
/** The category whose commands a focused or hovered region is about. */
const REGION_CATEGORY: Readonly<Record<string, string>> = {
  lane: 'Notes',
  motion: 'Motion',
  curve: 'Motion',
  lines: 'Lines',
  timing: 'Timing',
  preview: 'View',
  publish: 'Publish',
};

/** Where the user is, for ranking hints (UX-007). */
export interface HintSituation {
  readonly workspace: string;
  readonly tool: string;
  /** Focused (else hovered) region, or null. */
  readonly region: string | null;
}

/** The situation of a context: workspace, tool and the focused, else hovered, region. */
export function hintSituation(when: WhenContext): HintSituation {
  return { workspace: when.workspace, tool: when.tool, region: focusedRegion(when) ?? hoveredRegion(when) };
}

/** Rank of a command's hint in a situation, or null when it does not belong there. */
export function hintRank(
  def: Pick<CommandDef, 'id' | 'category' | 'hint'>,
  where: HintSituation,
): number | null {
  if (def.hint === undefined) return null;
  if (where.workspace !== 'chart' && CHART_CATEGORIES.has(def.category)) return null;
  const motionAdvice = def.category === 'Motion' || MOTION_HINT_IDS.has(def.id);
  if (motionAdvice && (where.region === null || !MOTION_REGIONS.has(where.region))) return null;
  let rank = def.hint;
  const tool = where.tool.toLowerCase();
  if (NOTE_TOOLS.has(tool)) {
    if (def.id.toLowerCase().includes(tool)) rank += 200;
    if (def.category === 'Motion') rank -= 150;
  } else if (tool === 'select') {
    // The active tool leads: what Select and the clipboard do before anything else (UX-007).
    if (def.category === 'Select' || def.category === 'Edit') rank += 150;
  }
  const regionCategory = where.region === null ? undefined : REGION_CATEGORY[where.region];
  if (regionCategory !== undefined) {
    if (def.category === regionCategory) rank += 100;
    else if (regionCategory === 'Motion' && def.category === 'Notes') rank -= 100;
    else if (regionCategory === 'Notes' && def.category === 'Motion') rank -= 100;
    else if (regionCategory === 'Notes' && (def.category === 'Select' || def.category === 'Edit')) rank += 50;
  }
  return rank;
}

/** Bindings that fire now, best ranked first for where the user is (one per command and arguments). */
export function hintsFor(services: AppServices, when: WhenContext, max = MAX_HINTS): HintItem[] {
  // A dialog or the palette has its own buttons; the editor's keys do not reach the editor meanwhile.
  if (when.has('dialog') || when.has('paletteOpen')) return [];
  const where = hintSituation(when);
  const seen = new Set<string>();
  const out: HintItem[] = [];
  for (const b of services.keymap.bindings()) {
    if (b.remove === true) continue;
    const def = services.registry.command(b.command);
    if (def === undefined) continue;
    const rank = hintRank(def, where);
    if (rank === null) continue;
    const key = `${b.command}\u0000${argsKey(b.args)}`;
    if (seen.has(key)) continue;
    let resolved;
    try {
      resolved = services.keymap.resolve(b.chord, when);
    } catch {
      resolved = null;
    }
    if (resolved?.command !== b.command || argsKey(resolved.args) !== argsKey(b.args)) continue;
    if (commandState(services, b.command, when, 'key').kind !== 'enabled') continue;
    seen.add(key);
    out.push({
      key,
      chord: b.chord,
      command: b.command,
      title: { key: def.title },
      rank,
      ...(def.hintSentence !== undefined ? { sentence: def.hintSentence } : {}),
    });
  }
  return out.sort((a, b) => b.rank - a.rank).slice(0, max);
}

/** The hovered region (pointer over a data-region), or null. */
export function hoveredRegion(when: WhenContext): string | null {
  for (const r of DATA_REGIONS) if (r !== 'dock' && when.has(`hover.${r}`)) return r;
  return when.has('hover.dock') ? 'dock' : null;
}

/** The region the hint bar describes: the focused one (a dock tab before the dock itself). */
export function focusedRegion(when: WhenContext): string | null {
  for (const r of DATA_REGIONS) if (r !== 'dock' && when.has(`focus.${r}`)) return r;
  return when.has('focus.dock') ? 'dock' : null;
}

const selectGestures = (snap: UiSnapshot): readonly GestureHint[] | null => snap.gestureHints;
const selectMini = (snap: UiSnapshot): boolean => snap.miniPreview;
const selectDrawer = (snap: UiSnapshot): boolean => snap.drawer;

/** Re-renders on DOM focus changes and when the pointer moves to another region. */
function useDomContextVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let region: string | null = null;
    let frame = 0;
    const bump = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setVersion((v) => v + 1);
      });
    };
    const onOver = (e: PointerEvent): void => {
      const r =
        e.target instanceof Element ? (e.target.closest('[data-region]')?.getAttribute('data-region') ?? null) : null;
      if (r !== region) {
        region = r;
        bump();
      }
    };
    document.addEventListener('focusin', bump, true);
    document.addEventListener('focusout', bump, true);
    document.addEventListener('pointerover', onOver, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('focusin', bump, true);
      document.removeEventListener('focusout', bump, true);
      document.removeEventListener('pointerover', onOver, true);
    };
  }, []);
  return version;
}

/** Re-renders after every committed change of the open project (enabled states depend on the document). */
function useCommitVersion(services: AppServices): number {
  const changed = useServicesChange(services);
  const [version, setVersion] = useState(0);
  const store = services.store;
  useEffect(() => {
    if (store === null) return undefined;
    return store.subscribeCommits(() => {
      setVersion((v) => v + 1);
    });
  }, [store, changed]);
  return version + changed * 100000;
}

/** How long the focused motion column's note shows after the column changes. */
export const COLUMN_NOTE_MS = 5000;

function FocusedColumnHint(props: { readonly services: AppServices; readonly text: ShellText }) {
  const { services, text } = props;
  const column = useEditorState(services, (st) => st.focusedColumn);
  // The column whose note has had its time on screen (set by the timer, never during render).
  const [expired, setExpired] = useState<typeof column>(null);
  useEffect(() => {
    if (column === null) return undefined;
    const timer = setTimeout(() => {
      setExpired(column);
    }, COLUMN_NOTE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [column]);
  const store = services.store;
  if (column === null || expired === column || store === null) return null;
  const name = store.doc.lines.get(column.line)?.name ?? String(column.line);
  return (
    <span className={s.hintColumn} data-hint-column="" data-hint-item="">
      {text.t(m.hintFocusedColumn, { prop: column.prop, line: name, layer: column.layer })}
    </span>
  );
}

/**
 * Hides the hint items that do not fit the row, whole (from the first one that overflows), so a hint is never cut
 * inside its key cap. Runs after every render and when the row changes size.
 */
function useWholeItems(row: RefObject<HTMLElement | null>, version: unknown): void {
  useLayoutEffect(() => {
    const el = row.current;
    if (el === null) return undefined;
    const fit = (): void => {
      const items = [...el.querySelectorAll<HTMLElement>(':scope > [data-hint-item]')];
      for (const item of items) item.hidden = false;
      const limit = el.clientWidth;
      if (!(limit > 0)) return;
      let over = false;
      for (const item of items) {
        if (!over && item.offsetLeft + item.offsetWidth > limit + 1) over = true;
        if (over) item.hidden = true;
      }
    };
    fit();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [row, version]);
}

/** The keymap's label of a chord (as the Kbd shows it), for spoken text. */
function keyLabel(services: AppServices, chord: string): string {
  try {
    return services.keymap.label(chord);
  } catch {
    return chord;
  }
}

/**
 * Message key of a command's hint sentence: its own `CommandDef.hintSentence`, else the convention of its title key
 * plus `Hint` ('notes.placeTap' -> 'notes.placeTapHint').
 */
export function hintSentenceKey(title: MessageRef, sentence?: string): string {
  return sentence ?? `${title.key}Hint`;
}

const KEYS_MARK = '\u0001';

/**
 * A hint in the sentences mode: the feature's sentence with the shortcut drawn where `{keys}` stands (at the start
 * when the sentence does not place it), else the shortcut and the command's full title.
 */
function HintSentence(props: { readonly services: AppServices; readonly text: ShellText; readonly hint: HintItem }) {
  const { services, text, hint } = props;
  const key = hintSentenceKey(hint.title, hint.sentence);
  if (!services.i18n.has(key)) {
    return (
      <>
        <Kbd chord={hint.chord} />
        <span>{text.tr(hint.title)}</span>
      </>
    );
  }
  const parts = text.t(key, { keys: KEYS_MARK }).split(KEYS_MARK);
  if (parts.length === 1) {
    return (
      <>
        <Kbd chord={hint.chord} />
        <span>{parts[0]}</span>
      </>
    );
  }
  return (
    <span className={s.hintSentenceText} data-sentence="">
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && <Kbd chord={hint.chord} />}
          {part}
        </Fragment>
      ))}
    </span>
  );
}

export function HintBar(props: { readonly services: AppServices; readonly ui: ShellUiService }) {
  const { services, ui } = props;
  const env = useShell();
  const text = useText(services);
  const settings = useSettingsValue(services);
  const gestures = useUi(ui, selectGestures);
  const editorVersion = useEditorState(
    services,
    (st) =>
      [st.selection, st.tool, st.placement, st.focusedColumn, st.activeLine, st.workspace, st.preview.theater] as const,
    (a, b) => a.every((x, i) => Object.is(x, b[i])),
  );
  const tool = useEditorState(services, (st) => st.tool);
  const workspace = useEditorState(services, (st) => st.workspace);
  const keymapVersion = useKeymapVersion(services);
  const domVersion = useDomContextVersion();
  const commitVersion = useCommitVersion(services);
  const playing = useTransportState(services.audio).playing;
  const viewport = useLayoutViewport(services);
  const chartLayout = computeChartLayout({
    viewport,
    preset: settings.editing.layout,
    level: settings.general.level,
  });
  const laptop = chartLayout.preset === 'laptop';
  const miniPreview = useUi(ui, selectMini);
  const drawer = useUi(ui, selectDrawer);
  const mode = settings.general.hintBar;

  const { hints, region, covered } = useMemo(() => {
    dependsOn(editorVersion, keymapVersion, domVersion, commitVersion, playing);
    if (mode === 'off') return { hints: [] as HintItem[], region: null, covered: false };
    const when = env.when();
    return {
      hints: hintsFor(services, when),
      region: focusedRegion(when),
      covered: when.has('dialog') || when.has('paletteOpen'),
    };
  }, [env, services, mode, editorVersion, keymapVersion, domVersion, commitVersion, playing]);
  const mainRef = useRef<HTMLDivElement>(null);
  useWholeItems(mainRef, [hints, gestures, mode, tool, region, covered]);

  const statusItems = services.registry.statusItems('statusbar');
  const sentences = mode === 'sentences';
  const projectOpen = services.store !== null;
  const showTool = sentences && projectOpen && workspace === 'chart' && !covered;
  const toolUse = showTool && gestures === null && NOTE_TOOLS.has(tool);
  const lead =
    covered || (region === null && !showTool)
      ? null
      : [
          ...(region === null ? [] : [text.t(m.hintRegion, { region })]),
          ...(showTool ? [text.t(m.hintTool, { tool })] : []),
        ].reduce((a, b) => text.t(m.listJoin, { a, b }));
  const gestureAnnouncement =
    gestures === null || gestures.length === 0
      ? ''
      : gestures
          .map((g) => text.t(m.hintGestureItem, { keys: keyLabel(services, g.chord), label: text.tr(g.label) }))
          .reduce((a, b) => text.t(m.listJoin, { a, b }));

  return (
    <footer className={s.hintBar} aria-label={text.t(m.hintBarLabel)} data-chrome="" data-hint-mode={mode}>
      <span className={s.srOnly} aria-live="polite" aria-atomic="true" data-hint-live="">
        {mode === 'off' ? '' : gestureAnnouncement}
      </span>
      <div className={s.hintMain} ref={mainRef}>
        {mode !== 'off' && (
          <>
            <span className={s.hintIcon} aria-hidden="true">
              <Icon name="Info" size={14} />
            </span>
            {!projectOpen && gestures === null && <span className={s.hintSentence}>{text.t(m.hintNoProject)}</span>}
            {sentences && lead !== null && (
              <span className={s.hintRegion} data-hint-lead="">
                {text.t(m.hintIn, { where: lead })}
              </span>
            )}
            {toolUse && (
              <span className={s.hintSentence} data-hint-item="" data-hint-tool="">
                {text.t(m.hintToolUse, { tool })}
              </span>
            )}
            {projectOpen && !covered && <FocusedColumnHint services={services} text={text} />}
            {gestures?.map((g, i) => (
              <span key={`g${String(i)}`} className={s.hintItem} data-gesture="" data-hint-item="">
                <Kbd chord={g.chord} />
                <span>{text.tr(g.label)}</span>
              </span>
            ))}
            {hints.map((h) => (
              <span key={h.key} className={s.hintItem} data-command={h.command} data-hint-item="">
                {sentences ? (
                  <HintSentence services={services} text={text} hint={h} />
                ) : (
                  <>
                    <Kbd chord={h.chord} />
                    <span className={s.hintShort}>{text.tr(h.title)}</span>
                  </>
                )}
              </span>
            ))}
          </>
        )}
      </div>
      <div className={s.hintSide}>
        <InlineSlot services={services} slot="statusbar" />
        {statusItems.map((item) => (
          <ErrorBoundary key={item.id} variant="silent" services={services} label={item.id}>
            <item.component services={services} />
          </ErrorBoundary>
        ))}
        {laptop && projectOpen && chartLayout.previewPlacement === 'floating' && (
          <IconButton
            icon="PictureInPicture2"
            label={{ key: m.cmdMiniPreview }}
            command="view.miniPreview"
            pressed={miniPreview}
            size="sm"
          />
        )}
        {laptop && projectOpen && (
          <IconButton icon="PanelRight" label={{ key: m.cmdDrawer }} command="view.drawer" pressed={drawer} size="sm" />
        )}
        <IconButton icon="Keyboard" label={{ key: m.hintCheatSheet }} command="help.cheatSheet" size="sm" />
      </div>
    </footer>
  );
}
